// Portal-orchestrated (approach A) execution engine for VSC Delete.
// Pure orchestration: all side effects are injected via `deps` so the substep
// sequence and partial-failure classification are unit-testable. Outcome
// vocabulary matches the PM-19637 audit contract.

export const SUBSTEP = { DELETE: 'DELETE' };
export const ATTEMPT = { SUCCEEDED: 'SUCCEEDED', FAILED: 'FAILED', PARTIAL: 'PARTIAL' };
export const OUTCOME = { SUCCESS: 'SUCCESS', FAILED: 'FAILED', UNKNOWN: 'UNKNOWN' };

const step = (name, outcome, detail = null) => ({ name, outcome, detail });

// Run one substep effect, classifying the result into an OUTCOME.
// Returns { outcome, detail, error? }. A thrown error with .unknown === true is
// UNKNOWN (bridge could not confirm); reader_not_found on DELETE is a safe
// already-deleted success; anything else is FAILED.
const runStep = async (name, effect) => {
    try {
        await effect();
        return { outcome: OUTCOME.SUCCESS, detail: null };
    } catch (e) {
        if (name === SUBSTEP.DELETE && e && e.code === 'vsc:reader_not_found') {
            return { outcome: OUTCOME.SUCCESS, detail: 'Card already deleted.' };
        }
        if (e.unknown === true) {
            return { outcome: OUTCOME.UNKNOWN, detail: (e && (e.msg || e.message)) || null, error: e };
        }
        return { outcome: OUTCOME.FAILED, detail: (e && (e.msg || e.message)) || null, error: e };
    }
};

// Auth-code gate (no-op in self-service). Returns null on pass, or a result
// object to short-circuit with when the gate rejects.
const gate = async (ctx, deps, results) => {
    try {
        if (deps.verifyAuthCode) await deps.verifyAuthCode(ctx);
        return null;
    } catch (e) {
        return { attemptStatus: ATTEMPT.FAILED, partialDetail: null, results };
    }
};

export async function executeDelete(ctx, deps) {
    const results = [];
    const blocked = await gate(ctx, deps, results);
    if (blocked) return blocked;

    const d = await runStep(SUBSTEP.DELETE, () => deps.deleteVsc(ctx.targetReader));
    results.push(step(SUBSTEP.DELETE, d.outcome, d.detail));

    let attemptStatus = ATTEMPT.SUCCEEDED;
    if (d.outcome === OUTCOME.FAILED) attemptStatus = ATTEMPT.FAILED;
    else if (d.outcome === OUTCOME.UNKNOWN) attemptStatus = ATTEMPT.PARTIAL;
    return { attemptStatus, partialDetail: null, results };
}
